import { type GovernanceUnitNode } from "~/api/types";
import { includesSearch } from "~/helpers/string";

// The acting governance unit: the one whose framework the user works in.
// Absent means the home unit, which is also what the API assumes when nothing
// is sent.
//
// The selection is a cookie, which is what the API reads: requests the browser
// makes on its own (images, embeds, downloads) cannot carry a header, and the
// cookie travels with all of them. The app reads the same cookie back, so
// there is one copy and nothing to keep in sync.
const GOVERNANCE_UNIT_COOKIE = "nh-governance-unit";
const GOVERNANCE_UNIT_CHANNEL = "governance-unit";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

export const getSelectedGovernanceUnitId = (): number | null => {
  try {
    const raw = document.cookie
      .split("; ")
      .find((entry) => entry.startsWith(`${GOVERNANCE_UNIT_COOKIE}=`))
      ?.slice(GOVERNANCE_UNIT_COOKIE.length + 1);
    if (raw === undefined) return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
};

export const setSelectedGovernanceUnitId = (id: number | null): void => {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    const value = id === null ? "" : String(id);
    const maxAge = id === null ? 0 : ONE_YEAR_SECONDS;
    document.cookie = `${GOVERNANCE_UNIT_COOKIE}=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
  } catch {
    // No document (tests) or cookies blocked; the app then views the home unit.
  }
  try {
    const channel = new BroadcastChannel(GOVERNANCE_UNIT_CHANNEL);
    channel.postMessage(id);
    channel.close();
  } catch {
    // No BroadcastChannel; other tabs then keep their view until reloaded.
  }
};

// Every API call reads the selection cookie, so a switch reloads the page to
// drop everything fetched for the previous unit.
export const switchGovernanceUnit = (id: number | null): void => {
  setSelectedGovernanceUnitId(id);
  reloadForGovernanceUnitSwitch();
};

/**
 * Where to land after the acting unit changes, or null to stay on the page.
 * A document belongs to one unit, so its pages would show "not found" in the
 * next one; the library is the closest page that exists in every unit.
 */
export const pathAfterGovernanceUnitSwitch = (
  pathname: string,
): string | null =>
  /^\/documents\/[^/]+/.test(pathname) ? "/documents" : null;

/** Reloads the app for the newly selected unit, leaving unit-specific pages. */
export const reloadForGovernanceUnitSwitch = (): void => {
  const path = pathAfterGovernanceUnitSwitch(window.location.pathname);
  if (path) {
    window.location.assign(path);
  } else {
    window.location.reload();
  }
};

/**
 * Calls `listener` when another tab changes the selection. The cookie is
 * shared across tabs, so a tab that keeps showing the previous unit would mix
 * units; the caller reloads. Returns the unsubscribe function.
 */
export const onGovernanceUnitChangedElsewhere = (
  listener: () => void,
): (() => void) => {
  try {
    const channel = new BroadcastChannel(GOVERNANCE_UNIT_CHANNEL);
    channel.onmessage = () => listener();
    return () => channel.close();
  } catch {
    return () => undefined;
  }
};

export const flattenGovernanceUnits = (
  nodes: GovernanceUnitNode[],
): GovernanceUnitNode[] =>
  nodes.flatMap((node) => [node, ...flattenGovernanceUnits(node.children)]);

// Ancestors are in the tree for context only. The home unit and its
// descendants are the units the user may act in, create under and rename.
export const isHomeOrDescendant = (node: GovernanceUnitNode): boolean =>
  !node.isAncestor;

export const homeAndDescendants = (
  nodes: GovernanceUnitNode[],
): GovernanceUnitNode[] =>
  flattenGovernanceUnits(nodes).filter(isHomeOrDescendant);

/** Ids from the root down to the unit, or empty when the unit is not below it. */
export const governanceUnitPathIds = (
  node: GovernanceUnitNode,
  id: number,
): number[] => {
  if (node.id === id) {
    return [id];
  }
  const below = node.children.flatMap((child) =>
    governanceUnitPathIds(child, id),
  );
  return below.length > 0 ? [node.id, ...below] : [];
};

interface GovernanceUnitRow {
  node: GovernanceUnitNode;
  depth: number;
}

/**
 * The rows to list. Without a search, the tree in order with the children of
 * collapsed units left out. With one, the matching units as a flat list.
 */
export const visibleGovernanceUnits = (
  root: GovernanceUnitNode,
  expandedIds: ReadonlySet<number>,
  search: string,
): GovernanceUnitRow[] => {
  if (search !== "") {
    return flattenGovernanceUnits([root])
      .filter((node) => includesSearch(node.name, search))
      .map((node) => ({ node, depth: 0 }));
  }

  const rowsOf = (
    node: GovernanceUnitNode,
    depth: number,
  ): GovernanceUnitRow[] => {
    const shownChildren = expandedIds.has(node.id) ? node.children : [];
    return [
      { node, depth },
      ...shownChildren.flatMap((child) => rowsOf(child, depth + 1)),
    ];
  };

  return rowsOf(root, 0);
};

export const homeGovernanceUnit = (
  nodes: GovernanceUnitNode[],
): GovernanceUnitNode | undefined =>
  flattenGovernanceUnits(nodes).find((node) => node.isHome);

const withoutGovernanceUnit = (
  node: GovernanceUnitNode,
  id: number,
): GovernanceUnitNode => ({
  ...node,
  children: node.children
    .filter((child) => child.id !== id)
    .map((child) => withoutGovernanceUnit(child, id)),
});

export interface NewParentOptions {
  currentParent: GovernanceUnitNode;
  possibleParents: GovernanceUnitNode;
}

/**
 * The home unit's tree without the unit's own subtree, where a move would be a
 * cycle. Null for a unit that cannot move, like the home unit's ancestors.
 */
export const newParentOptions = (
  nodes: GovernanceUnitNode[],
  unit: GovernanceUnitNode,
): NewParentOptions | null => {
  const home = homeGovernanceUnit(nodes);
  if (!home) return null;

  const currentParent = flattenGovernanceUnits([home]).find((candidate) =>
    candidate.children.some((child) => child.id === unit.id),
  );
  if (!currentParent) return null;

  return {
    currentParent,
    possibleParents: withoutGovernanceUnit(home, unit.id),
  };
};
